import { api } from '../api.js';

const PAGE_SIZE = 50;

const ENTITY_TYPES = ['', 'project', 'task', 'dependency', 'resource'];
const USER_IDS = ['', 'ruslan', 'mentor'];

let _state = {
  filters: {},
  offset: 0,
  rows: [],
  expanded: new Set(),
  actions: [],
};

let _container = null;

export async function initAudit(container) {
  _container = container;
  _state = { filters: {}, offset: 0, rows: [], expanded: new Set(), actions: [] };

  container.innerHTML = '<div class="loading-spinner">Загрузка истории изменений...</div>';

  // Get distinct actions for filter dropdown via changes-summary (last 90 days).
  try {
    const summary = await api.auditChangesSummary(90);
    _state.actions = Array.from(new Set((summary.by_action ?? []).map(r => r.action))).sort();
  } catch { /* ignore — list still works */ }

  await loadPage(true);
  render();
}

async function loadPage(reset) {
  if (reset) {
    _state.offset = 0;
    _state.rows = [];
  }
  const params = { ...buildQuery(), limit: PAGE_SIZE, offset: _state.offset };
  const rows = await api.audit(params);
  _state.rows = reset ? rows : _state.rows.concat(rows);
  _state.offset += rows.length;
  _state.lastPageSize = rows.length;
}

function buildQuery() {
  const q = {};
  for (const [k, v] of Object.entries(_state.filters)) {
    if (v != null && v !== '') q[k] = v;
  }
  return q;
}

function render() {
  _container.innerHTML = `
    <div class="audit-root">
      <h2 style="margin-bottom: var(--space-3); font-size: var(--text-xl);">📜 История изменений</h2>
      <div class="audit-toolbar">
        <label>Пользователь
          <select name="user_id">
            ${USER_IDS.map(u => `<option value="${u}" ${_state.filters.user_id === u ? 'selected' : ''}>${u || 'все'}</option>`).join('')}
          </select>
        </label>
        <label>Действие
          <select name="action">
            <option value="">все</option>
            ${_state.actions.map(a => `<option value="${a}" ${_state.filters.action === a ? 'selected' : ''}>${a}</option>`).join('')}
          </select>
        </label>
        <label>Сущность
          <select name="entity_type">
            ${ENTITY_TYPES.map(t => `<option value="${t}" ${_state.filters.entity_type === t ? 'selected' : ''}>${t || 'все'}</option>`).join('')}
          </select>
        </label>
        <label>От
          <input type="date" name="from" value="${_state.filters.from ?? ''}">
        </label>
        <label>До
          <input type="date" name="to" value="${_state.filters.to ?? ''}">
        </label>
        <label>Поиск
          <input type="text" name="search" placeholder="текст в old/new value" value="${_state.filters.search ?? ''}">
        </label>
        <button id="audit-apply">Применить</button>
        <button id="audit-reset">Сброс</button>
        <button id="audit-csv">Скачать CSV</button>
      </div>
      <div class="audit-list" id="audit-list">
        ${renderRows()}
      </div>
    </div>
  `;

  attachEvents();
}

function renderRows() {
  if (!_state.rows.length) {
    return '<div class="audit-empty">Записей не найдено</div>';
  }
  const rows = _state.rows.map(renderRow).join('');
  const more = _state.lastPageSize === PAGE_SIZE
    ? `<button class="audit-load-more" id="audit-load-more">Загрузить ещё ${PAGE_SIZE}</button>`
    : '';
  return rows + more;
}

function renderRow(r) {
  const ts = formatTime(r.timestamp);
  const target = `${r.entity_type}#${r.entity_id ?? '—'}`;
  const expanded = _state.expanded.has(r.id);
  return `
    <div class="audit-row" data-id="${r.id}">
      <div class="audit-row-head">
        <span class="audit-row-time">${escHtml(ts)}</span>
        <span class="audit-row-user">${escHtml(r.user_id ?? '')}</span>
        <span class="audit-row-source">[${escHtml(r.source ?? '')}]</span>
        <span class="audit-row-action">${escHtml(r.action ?? '')}</span>
        <span class="audit-row-target">${escHtml(target)}</span>
      </div>
      ${expanded ? `
        <div class="audit-diff">
          <div>
            <h4>old_value</h4>
            <pre>${escHtml(formatJson(r.old_value))}</pre>
          </div>
          <div>
            <h4>new_value</h4>
            <pre>${escHtml(formatJson(r.new_value))}</pre>
          </div>
        </div>
      ` : ''}
    </div>
  `;
}

function attachEvents() {
  const root = _container;

  root.querySelector('#audit-apply').addEventListener('click', async () => {
    captureFiltersFromDom();
    await loadPage(true);
    render();
  });

  root.querySelector('#audit-reset').addEventListener('click', async () => {
    _state.filters = {};
    await loadPage(true);
    render();
  });

  root.querySelector('#audit-csv').addEventListener('click', () => {
    captureFiltersFromDom();
    const url = api.auditExportCsvUrl(buildQuery());
    window.open(url, '_blank');
  });

  const more = root.querySelector('#audit-load-more');
  if (more) {
    more.addEventListener('click', async () => {
      await loadPage(false);
      render();
    });
  }

  root.querySelector('#audit-list').addEventListener('click', (e) => {
    const row = e.target.closest('.audit-row');
    if (!row) return;
    const id = Number(row.dataset.id);
    if (_state.expanded.has(id)) _state.expanded.delete(id);
    else _state.expanded.add(id);
    render();
  });
}

function captureFiltersFromDom() {
  const root = _container;
  for (const name of ['user_id', 'action', 'entity_type', 'from', 'to', 'search']) {
    const el = root.querySelector(`[name="${name}"]`);
    _state.filters[name] = el ? el.value.trim() : '';
  }
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatJson(v) {
  if (v == null) return '—';
  try { return JSON.stringify(v, null, 2); }
  catch { return String(v); }
}

function escHtml(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
